import styled from "@emotion/styled";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { authSelector } from "../redux/slices/authSlice";

const CLOUD_BEDS_SCRIPT =
  "https://hotels.cloudbeds.com/app_button/beesql_QSdCXPzsq280LVm75tfbBnKJ";
const CLOUD_BEDS_REDIRECT_URI = "https://ohip.beesql.com/cloudbedsoAuth2";
const CLOUD_BEDS_STATUS_API =
  "https://reportapi.beesql.com/api/admin/retailr/report";
const CLOUD_BEDS_STATUS_XOPTION = "SP_beeSQLCloudbedsAuth";
const CLOUD_BEDS_STATUS_KEY = "cloudbedsConnectionStatus";

const Wrapper = styled.div`
  padding: 16px;
  flex-grow: 1;
`;

const Panel = styled.div`
  max-width: 640px;
  border: 1px solid rgba(35, 42, 59, 0.14);
  border-radius: 8px;
  background: #ffffff;
  padding: 20px;
`;

const Content = styled.div`
  max-width: 1100px;
  margin-top: 40px;
`;

const Title = styled.h2`
  margin: 0 0 12px;
  color: #232a3b;
  font-size: 22px;
  font-weight: 600;
`;

const Text = styled.p`
  margin: 0 0 10px;
  color: #374151;
  line-height: 1.5;
`;

const Hero = styled.section`
  text-align: center;
  padding: 48px 20px 28px;

  h1 {
    margin: 0 0 20px;
    color: #111827;
    font-size: 42px;
    line-height: 1.15;
  }

  p {
    max-width: 700px;
    margin: 0 auto;
    color: #555;
    font-size: 18px;
    line-height: 1.55;
  }
`;

const VideoHighlight = styled.div`
  max-width: 820px;
  margin: 35px auto 0;
  text-align: left;

  h2 {
    margin: 0 0 12px;
    text-align: center;
    color: #111827;
    font-size: 26px;
  }
`;

const VideoFrame = styled.a`
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 8px;
  background: #111111;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.18);

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoPlay = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 82px;
  height: 58px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  transform: translate(-50%, -50%);

  &::before {
    content: "";
    width: 0;
    height: 0;
    margin-left: 6px;
    border-top: 13px solid transparent;
    border-bottom: 13px solid transparent;
    border-left: 20px solid #111111;
  }
`;

const VideoCaption = styled.p`
  margin: 10px 0 0;
  text-align: center;
  color: #555555;
  font-size: 14px;
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-top: 60px;
`;

const Feature = styled.div`
  border-radius: 8px;
  background: #ffffff;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);

  h3 {
    margin: 0 0 10px;
    color: #111827;
  }

  p {
    margin: 0;
    color: #555555;
    line-height: 1.5;
  }
`;

const Section = styled.section`
  margin-top: 80px;

  h2 {
    margin: 0 0 14px;
    color: #111827;
  }

  p {
    color: #374151;
    line-height: 1.6;
  }
`;

const SupportArticle = styled.article`
  margin-top: 24px;
  border-radius: 8px;
  background: #ffffff;
  padding: 35px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);

  h2 {
    margin-top: 0;
  }

  h3 {
    margin-top: 30px;
  }

  p,
  li {
    color: #374151;
    line-height: 1.6;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: #0070f3;
  }
`;

const MappingScreenshot = styled.figure`
  max-width: 520px;
  box-sizing: border-box;
  margin: 20px 0 0;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  background: #ffffff;
  padding: 14px;
  color: #0f172a;

  h4 {
    margin: 0 0 14px;
    font-size: 20px;
  }
`;

const ConnectionStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({ connected }) =>
    connected ? "rgba(22, 163, 74, 0.1)" : "rgba(220, 38, 38, 0.1)"};
  color: ${({ connected }) => (connected ? "#15803d" : "#b91c1c")};
  font-weight: 600;
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ connected }) => (connected ? "#16a34a" : "#dc2626")};
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 16px 0;
  min-height: 37px;
  flex-wrap: wrap;
`;

const ButtonMount = styled.div`
  height: 37px;
  line-height: 0;
`;

const DisconnectButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 37px;
  height: 37px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #dc2626;
  border-radius: 6px;
  background: #dc2626;
  color: #ffffff;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #b91c1c;
  }

  &:disabled {
    border-color: #9ca3af;
    background: #9ca3af;
    cursor: wait;
  }
`;

const PreviewConnectButton = styled.button`
  height: 37px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: #0b74b8;
  color: #ffffff;
  font-weight: 700;
`;

const Status = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-left: 4px solid ${({ error }) => (error ? "#dc2626" : "#5187e0")};
  background: ${({ error }) =>
    error ? "rgba(220, 38, 38, 0.08)" : "rgba(81, 135, 224, 0.08)"};
  color: #232a3b;
`;

const getConnectedFromStatusResponse = (responseBody) => {
  if (!responseBody || String(responseBody.status || "").toUpperCase() !== "OK") {
    return false;
  }

  if (typeof responseBody.connected === "boolean") {
    return responseBody.connected;
  }

  const statusText = String(
    responseBody.connection_status ||
      responseBody.connectionStatus ||
      responseBody.cloudbeds_status ||
      ""
  ).toLowerCase();

  return statusText === "connected";
};

const addTokenStateToCloudbedsButton = (buttonMount, token) => {
  const button = buttonMount.querySelector(".cloudbeds-app-button");

  if (!button || !token) {
    return;
  }

  const url = new URL(button.href);
  url.searchParams.set("redirect_uri", CLOUD_BEDS_REDIRECT_URI);
  url.searchParams.set("state", `x_token:${token}`);
  button.href = url.toString();
};

const Cloudbeds = () => {
  const buttonMountRef = useRef(null);
  const { token } = useSelector(authSelector);
  const [connected, setConnected] = useState(
    () => localStorage.getItem(CLOUD_BEDS_STATUS_KEY) === "connected"
  );
  const [statusMessage, setStatusMessage] = useState(
    "Checking Cloudbeds connection status..."
  );
  const [statusError, setStatusError] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const getCloudbedsStatus = useCallback(async () => {
    setStatusMessage("Checking Cloudbeds connection status...");
    setStatusError(false);

    try {
      const response = await fetch(CLOUD_BEDS_STATUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          xoption: CLOUD_BEDS_STATUS_XOPTION,
        },
        body: JSON.stringify({
          x_token: token,
          xoption: CLOUD_BEDS_STATUS_XOPTION,
          xoperation: "status",
        }),
      });

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok || String(responseBody.status || "").toUpperCase() !== "OK") {
        throw new Error(
          responseBody.error_message ||
            responseBody.errormessage ||
            responseBody.error ||
            responseBody.message ||
            "Failed to check Cloudbeds connection status"
        );
      }

      const nextConnected = getConnectedFromStatusResponse(responseBody);

      setConnected(nextConnected);
      setStatusMessage("Cloudbeds connection status loaded.");
      localStorage.setItem(
        CLOUD_BEDS_STATUS_KEY,
        nextConnected ? "connected" : "disconnected"
      );
    } catch (error) {
      setConnected(false);
      setStatusError(true);
      setStatusMessage(
        error && error.message
          ? error.message
          : "Failed to check Cloudbeds connection status"
      );
      localStorage.setItem(CLOUD_BEDS_STATUS_KEY, "disconnected");
    }
  }, [token]);

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    setStatusError(false);
    setStatusMessage("Disconnecting Cloudbeds app...");

    try {
      const response = await fetch(CLOUD_BEDS_STATUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          xoption: CLOUD_BEDS_STATUS_XOPTION,
        },
        body: JSON.stringify({
          x_token: token,
          xoption: CLOUD_BEDS_STATUS_XOPTION,
          xoperation: "disconnect",
        }),
      });

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok || String(responseBody.status || "").toUpperCase() !== "OK") {
        throw new Error(
          responseBody.error_message ||
            responseBody.errormessage ||
            responseBody.error ||
            responseBody.message ||
            "Failed to disconnect Cloudbeds app"
        );
      }

      setConnected(false);
      setStatusMessage("Cloudbeds app disconnected.");
      localStorage.setItem(CLOUD_BEDS_STATUS_KEY, "disconnected");
    } catch (error) {
      setStatusError(true);
      setStatusMessage(
        error && error.message ? error.message : "Failed to disconnect Cloudbeds app"
      );
    } finally {
      setDisconnecting(false);
    }
  }, [token]);

  useEffect(() => {
    const buttonMount = buttonMountRef.current;

    if (!buttonMount) {
      return undefined;
    }

    const originalWrite = document.write;
    const script = document.createElement("script");

    document.write = (html) => {
      buttonMount.insertAdjacentHTML("beforeend", html);
      addTokenStateToCloudbedsButton(buttonMount, token);
    };

    script.src = CLOUD_BEDS_SCRIPT;
    script.async = true;
    script.onload = () => {
      document.write = originalWrite;
    };
    script.onerror = () => {
      document.write = originalWrite;
    };

    buttonMount.appendChild(script);
    addTokenStateToCloudbedsButton(buttonMount, token);

    return () => {
      document.write = originalWrite;
      buttonMount.innerHTML = "";
    };
  }, [token]);

  useEffect(() => {
    getCloudbedsStatus();
  }, [getCloudbedsStatus]);

  useEffect(() => {
    window.addEventListener("cloudbeds:refresh-status", getCloudbedsStatus);

    return () => {
      window.removeEventListener("cloudbeds:refresh-status", getCloudbedsStatus);
    };
  }, [getCloudbedsStatus]);

  useEffect(() => {
    const handleCloudbedsMessage = (event) => {
      if (event.data?.type !== "Cloudbeds") {
        return;
      }

      const nextConnected = event.data.status === "success";
      setConnected(nextConnected);
      localStorage.setItem(
        CLOUD_BEDS_STATUS_KEY,
        nextConnected ? "connected" : "disconnected"
      );
      setStatusError(false);
      setStatusMessage(
        nextConnected
          ? "Cloudbeds OAuth completed successfully."
          : "Cloudbeds OAuth failed."
      );
    };

    window.addEventListener("message", handleCloudbedsMessage);

    return () => {
      window.removeEventListener("message", handleCloudbedsMessage);
    };
  }, []);

  return (
    <Wrapper>
      <Panel>
        <Title>Cloudbeds OAuth2 process</Title>
        <ConnectionStatus connected={connected}>
          <StatusDot connected={connected} />
          {connected ? "Connected" : "Disconnected"}
        </ConnectionStatus>
        <ButtonRow>
          <ButtonMount ref={buttonMountRef} />
          {connected ? (
            <DisconnectButton
              disabled={disconnecting}
              onClick={handleDisconnect}
              type="button"
            >
              {disconnecting ? "Disconnecting..." : "Disconnect the app"}
            </DisconnectButton>
          ) : null}
        </ButtonRow>
        <Text>Cloudbeds integration is ready for OAuth2 authorization.</Text>
        <Status error={statusError}>{statusMessage}</Status>
      </Panel>

      <Content>
        <Hero>

          <VideoHighlight>
            <h2>Watch the Integration Video</h2>
            <VideoFrame
              aria-label="Watch beeSQL Cloudbeds integration video on YouTube"
              href="https://youtu.be/ewtslMRLj4A"
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt="beeSQL Cloudbeds integration video preview"
                src="https://img.youtube.com/vi/ewtslMRLj4A/maxresdefault.jpg"
              />
              <VideoPlay />
            </VideoFrame>
            <VideoCaption>Click the preview to watch the video on YouTube.</VideoCaption>
          </VideoHighlight>
        </Hero>

        <Features>
          <Feature>
            <h3>Instant Room Charges</h3>
            <p>Post room charges directly from POS to guest folios without manual entry.</p>
          </Feature>
          <Feature>
            <h3>Revenue Sync</h3>
            <p>Automatically sync daily revenue between POS and PMS systems.</p>
          </Feature>
          <Feature>
            <h3>Guest Lookup</h3>
            <p>Search guests and reservations instantly from your POS interface.</p>
          </Feature>
          <Feature>
            <h3>Multi-Location Support</h3>
            <p>Centralized management for hotels, resorts, and chains.</p>
          </Feature>
          <Feature>
            <h3>Secure Integration</h3>
            <p>Enterprise-grade security with encrypted data transfer.</p>
          </Feature>
          <Feature>
            <h3>Audit & Reporting</h3>
            <p>Track every transaction with detailed logging and reporting.</p>
          </Feature>
        </Features>

        <Section>
          <h2>Who Is This For?</h2>
          <p>
            This app is designed for hotels, resorts, and hospitality businesses that
            need seamless integration between POS and PMS systems. It reduces manual
            work, improves accuracy, and ensures reliable revenue tracking.
          </p>
        </Section>

        <Section>
          <h2>Integration Support Article</h2>
        </Section>

        <SupportArticle>
          <h2>beeSQL Room Charge & Revenue Sync for Cloudbeds</h2>
          <p>
            beeSQL connects Cloudbeds with supported POS and accounting workflows so
            hotels can look up guests, post room charges, and sync revenue with fewer
            manual steps.
          </p>

          <h3>How to sign up for beeSQL</h3>
          <p>
            To sign up, contact beeSQL at <a href="mailto:tom@beesql.com">tom@beesql.com</a>
            or use the Request Demo button on this page. Our team will review your
            property, POS system, required workflows, and Cloudbeds account setup before
            activating the integration.
          </p>

          <h3>How to connect beeSQL with Cloudbeds for the first time</h3>
          <ol>
            <li>Sign up with beeSQL and complete the onboarding requirements for your property.</li>
            <li>Open Cloudbeds Marketplace from your Cloudbeds account.</li>
            <li>Find the beeSQL Room Charge & Revenue Sync app.</li>
            <li>Click Connect App and approve the requested Cloudbeds permissions.</li>
            <li>
              After authorization, beeSQL will configure the POS mappings, payment
              types, revenue codes, and house accounts needed for your property.
            </li>
            <li>beeSQL will run a test transaction with your team before production use.</li>
          </ol>

          <h3>Specific functionality of the integration</h3>
          <ul>
            <li>Guest and reservation lookup from the connected POS workflow.</li>
            <li>Posting room charges from POS to Cloudbeds guest folios.</li>
            <li>Daily revenue posting and revenue sync between POS and Cloudbeds.</li>
            <li>Support for mapped payment methods, house accounts, revenue centers, and departments.</li>
            <li>Transaction logging for audit and troubleshooting.</li>
            <li>Multi-location configuration when enabled during onboarding.</li>
          </ul>

          <h3>Limitations</h3>
          <ul>
            <li>The integration requires an active Cloudbeds account and supported POS configuration.</li>
            <li>Room charge and revenue posting depend on mappings configured during onboarding.</li>
            <li>The integration does not replace Cloudbeds user permissions or accounting review.</li>
            <li>Historical imports and custom accounting rules must be reviewed before activation.</li>
          </ul>

          <h3>How to disconnect the apps</h3>
          <p>
            To disconnect beeSQL from Cloudbeds, go to Cloudbeds PMS and remove the app
            from your connected apps list. Cloudbeds provides step-by-step instructions
            here:{" "}
            <a
              href="https://myfrontdesk.cloudbeds.com/hc/en-us/articles/219774407-How-to-disconnect-an-app-from-Cloudbeds-PMS"
              rel="noopener noreferrer"
              target="_blank"
            >
              How to disconnect an app from Cloudbeds PMS
            </a>
            .
          </p>

          <h3>How disconnect works from the beeSQL side</h3>
          <p>
            When Cloudbeds access is revoked, beeSQL stops making API calls for that
            property. You can also disconnect the integration from the beeSQL Mapping App
            by opening the Cloudbeds OAuth2 process screen and clicking Disconnect the app.
          </p>

          <ol>
            <li>Open the beeSQL Mapping App.</li>
            <li>Go to the Cloudbeds OAuth2 process section.</li>
            <li>Confirm that the status is Connected.</li>
            <li>Click Disconnect the app.</li>
            <li>
              Contact <a href="mailto:tom@beesql.com">tom@beesql.com</a> if retained
              transaction logs must be reviewed.
            </li>
          </ol>

          <MappingScreenshot>
            <h4>Cloudbeds OAuth2 process</h4>
            <ConnectionStatus connected>
              <StatusDot connected />
              Connected
            </ConnectionStatus>
            <ButtonRow>
              <PreviewConnectButton type="button">Connect to Cloudbeds</PreviewConnectButton>
              <DisconnectButton type="button">Disconnect the app</DisconnectButton>
            </ButtonRow>
            <Text>Cloudbeds integration is ready for OAuth2 authorization.</Text>
            <Status>Cloudbeds connection status loaded.</Status>
          </MappingScreenshot>

          <h3>How to get support</h3>
          <p>
            For setup, transaction, mapping, or disconnection support, email{" "}
            <a href="mailto:tom@beesql.com">tom@beesql.com</a>. Please include your
            property name, Cloudbeds property ID if available, POS location, transaction
            date, and a short description of the issue.
          </p>

          <h3>FAQs</h3>
          <p>
            FAQs can be added after production testing and onboarding are complete. A
            short customer-facing integration video is available here:{" "}
            <a href="https://youtu.be/ewtslMRLj4A" rel="noopener noreferrer" target="_blank">
              beeSQL Cloudbeds integration video
            </a>
            .
          </p>
        </SupportArticle>
 
      </Content>
    </Wrapper>
  );
};

export default Cloudbeds;
